/**
 * Content Timeline Layout
 * コンテンツノードを時系列に沿って配置するレイアウト
 */

import { Node, Edge } from '@xyflow/react';
import { TopicNode } from '@/types/nodeTypes';
import { TopicEdge } from '@/types/edgeTypes';
import { ContentNode, timestampToSeconds } from '@/types/contentTypes';
import { Phase } from '@/types/phaseTypes';

/**
 * タイムライン上にコンテンツノードを配置する
 */
export function applyContentTimelineLayout(
  contentNodes: ContentNode[],
  width: number = 1000,
  height: number = 600,
  topicNodes: TopicNode[] = [],
  phases: Phase[] = []
): ContentNode[] {
  if (!contentNodes.length) return [];

  // ノードをタイムスタンプでソート
  const sortedNodes = [...contentNodes].sort((a, b) => 
    a.timestampSeconds - b.timestampSeconds
  );
  
  // タイムラインの開始と終了時間を取得
  const startTime = sortedNodes[0]?.timestampSeconds || 0;
  const endTime = sortedNodes[sortedNodes.length - 1]?.timestampSeconds || 3600; // デフォルト1時間
  
  // 時間範囲
  const timeRange = Math.max(endTime - startTime, 60); // 最低1分
  
  // 水平方向の余白
  const horizontalMargin = 100;
  
  // 各トピックIDごとのレーンを決定（Y座標）
  const topicLanes = new Map<string, number>();
  const laneHeight = 120; // レーンの高さ
  const usedTopicIds = new Set<string>();
  
  // 使用されているトピックIDを取得
  sortedNodes.forEach(node => {
    usedTopicIds.add(node.topicId);
  });
  
  // トピックのフェーズごとにグループ化
  const topicsByPhase = new Map<string, TopicNode[]>();
  
  usedTopicIds.forEach(topicId => {
    const topic = topicNodes.find(t => t.id === topicId);
    if (topic) {
      const phaseId = topic.phaseId;
      if (!topicsByPhase.has(phaseId)) {
        topicsByPhase.set(phaseId, []);
      }
      topicsByPhase.get(phaseId)?.push(topic);
    }
  });
  
  // フェーズの順番を決定（仕様書のフェーズ順）
  const phaseOrder = [
    'fact_action', 'fact_detail', 'thought_feeling', 'value', 'career_view', 'company'
  ];
  
  // レーン割り当て
  let currentLane = 0;
  phaseOrder.forEach(phaseId => {
    const topicsInPhase = topicsByPhase.get(phaseId) || [];
    
    // フェーズ内のトピックを重要度順にソート
    topicsInPhase.sort((a, b) => b.importance - a.importance);
    
    // フェーズセパレータのスペース
    if (topicsInPhase.length > 0) {
      currentLane += 0.5; // フェーズ間の余白
    }
    
    // トピックごとにレーン割り当て
    topicsInPhase.forEach(topic => {
      topicLanes.set(topic.id, currentLane);
      currentLane += 1;
    });
  });
  
  // 使用するトピックでレーンが見つからない場合、最後に追加
  usedTopicIds.forEach(topicId => {
    if (!topicLanes.has(topicId)) {
      topicLanes.set(topicId, currentLane);
      currentLane += 1;
    }
  });
  
  // 利用可能な幅（左右のマージンを除く）
  const availableWidth = width - (horizontalMargin * 2);
  
  // 各ノードに位置を割り当て
  return sortedNodes.map(node => {
    // X位置：タイムスタンプに基づいて計算
    const relativeTime = node.timestampSeconds - startTime;
    const xPosition = horizontalMargin + (relativeTime / timeRange) * availableWidth;
    
    // Y位置：トピックのレーンに基づいて計算
    const lane = topicLanes.get(node.topicId) || 0;
    const yPosition = lane * laneHeight + 100; // 上部に余白を持たせる
    
    return {
      ...node,
      position: {
        x: xPosition,
        y: yPosition
      }
    };
  });
}

/**
 * トピックノードからコンテンツノードを抽出して
 * タイムラインレイアウトを適用する
 */
export function generateContentTimelineLayout(
  nodes: TopicNode[],
  edges: TopicEdge[],
  phases: Phase[],
  width: number = 1000,
  height: number = 600
): { nodes: Node[], edges: Edge[] } {
  // アクティブなノードだけから内容を抽出
  const activeNodes = nodes.filter(node => node.active);
  
  // コンテンツノードの配列を作成
  const contentNodes: ContentNode[] = [];
  
  activeNodes.forEach(node => {
    if (node.contents && node.contents.length > 0) {
      node.contents.forEach((content, index) => {
        contentNodes.push({
          id: `content-${node.id}-${index}`,
          topicId: node.id,
          content: content.content,
          timestamp: content.timestamp,
          timestampSeconds: timestampToSeconds(content.timestamp),
          status: 'success',
          width: 220,
          height: 100
        });
      });
    }
  });
  
  // タイムラインレイアウトを適用
  const positionedContentNodes = applyContentTimelineLayout(
    contentNodes,
    width,
    height,
    nodes,
    phases
  );
  
  // ReactFlow用のノードとエッジに変換
  const rfNodes: Node[] = positionedContentNodes.map(node => ({
    id: node.id,
    type: 'contentNode',
    position: node.position || { x: 0, y: 0 },
    data: {
      topicId: node.topicId,
      content: node.content,
      timestamp: node.timestamp,
      timestampSeconds: node.timestampSeconds,
      status: node.status,
      width: node.width,
      height: node.height
    }
  }));
  
  // タイムラインの区切り線をエッジとして表現
  const timelineEdges: Edge[] = [];
  
  // エッジはここでは作成しないので空配列を返す
  return {
    nodes: rfNodes,
    edges: timelineEdges
  };
}